import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { NgClass } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { forkJoin, Subject } from 'rxjs';
import { throttleTime } from 'rxjs/operators';
import { ApiService } from 'app/services/api.service';
import { PermissionService } from 'app/services/permission.service';
import {
  ACCESS_LEVEL_RANK, AccessLevel, Areas, BUILT_IN_ROLE_NAME_KEYS, IAccessRole, IAccessRoleSettings, IArea, ISaveAccessRole,
  resolveGrant
} from 'app/models/permission.model';
import { DeleteRoleDialogComponent } from './delete-role-dialog.component';
import { PAGE_ACCESS } from 'app/services/page-access';
import {
  AreaChoice, AreaRow, countAtLeast, effectiveChoice, ROLE_ICONS, toChoices, toGrants, toRows, unreachableFrom
} from './role-editor.model';

/** Areas that have a page of their own, so their sub-areas don't need the parent (page-access.ts) */
const OWN_PAGE_AREAS: ReadonlySet<string> = new Set(
  Object.values(PAGE_ACCESS).map((page: { area?: string }) => page.area).filter((area): area is string => !!area));

/**
 * Roles (ES-7489): the built-in roles (read-only) and the company's custom roles, with an editor that
 * sets No access / View / View & edit per area. The API enforces every rule; the page mirrors the ones
 * the user can act on (nobody grants more than they have, the role limit, custom roles not enabled).
 */
@Component({
  selector: 'app-roles',
  templateUrl: './roles.component.html',
  styleUrls: ['./roles.component.css'],
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [NgClass, FormsModule, ReactiveFormsModule, MatButtonToggleModule, MatFormFieldModule, MatSelectModule, MatTooltipModule, TranslateModule]
})
export class RolesComponent implements OnInit, OnDestroy {
  readonly icons = ROLE_ICONS;
  readonly builtInNameKeys = BUILT_IN_ROLE_NAME_KEYS;

  loading = true;
  loadError = false;
  settings: IAccessRoleSettings = { customRolesEnabled: false, maxCustomRoles: 0 };
  areas: IArea[] = [];
  rows: AreaRow[] = [];
  roles: IAccessRole[] = [];

  selected: IAccessRole | null = null;
  isNew = false;
  form: UntypedFormGroup;
  choices: Record<string, AreaChoice> = {};
  choicesDirty = false;
  /** Main areas whose sub-areas are shown */
  expanded = new Set<string>();

  saveSuccess = false;
  saveError: string | null = null;
  formSubmitSubject = new Subject<void>();

  constructor(
    private api: ApiService,
    private permissions: PermissionService,
    private translate: TranslateService,
    private fb: UntypedFormBuilder,
    private route: ActivatedRoute,
    private location: Location,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.formSubmitSubject.pipe(throttleTime(3000)).subscribe(() => this.save());
    this.load(Number(this.route.snapshot.paramMap.get('id')) || null);
  }

  ngOnDestroy(): void {
    this.formSubmitSubject.unsubscribe();
  }

  /** The user can change roles (the API decides; this only hides the controls) */
  get canEdit(): boolean {
    return this.settings.customRolesEnabled && this.permissions.has(Areas.StaffAccessRights, 'ReadWrite');
  }

  get customRoleCount(): number {
    return this.roles.filter(r => !r.builtIn).length;
  }

  get limitReached(): boolean {
    return this.customRoleCount >= this.settings.maxCustomRoles;
  }

  get readOnly(): boolean {
    return !this.selected || this.selected.builtIn || !this.canEdit;
  }

  /**
   * Load the roles, optionally reopening one. After a save, the reopened role keeps the "saved" message
   * (opening a role otherwise clears it).
   */
  load(selectId: number | null = null, afterSave = false): void {
    this.loading = true;
    forkJoin({
      settings: this.api.getAccessRoleSettings(),
      areas: this.api.getPermissionAreas(),
      roles: this.api.getAccessRoles()
    }).subscribe({
      next: ({ settings, areas, roles }) => {
        this.settings = settings;
        this.areas = areas;
        this.rows = toRows(areas);
        this.roles = roles;
        this.loading = false;
        this.loadError = false;
        if (selectId) {
          const role = roles.find(r => r.id === selectId);
          if (role) this.select(role);
          if (role && afterSave) this.saveSuccess = true;
        }
      },
      error: () => {
        this.loading = false;
        this.loadError = true;
      }
    });
  }

  /** Display name of a role: built-in roles by their translated level name */
  roleName(role: IAccessRole): string {
    if (!role.builtIn) return role.name;
    const key = this.builtInNameKeys[role.level];
    return key ? this.translate.instant(key) : role.name;
  }

  roleIcon(role: IAccessRole): string {
    return role.builtIn ? 'fa-lock' : (role.icon || 'fa-user');
  }

  /** Short access summary for the list, e.g. "12 view · 5 edit" */
  summary(role: IAccessRole): { view: number; edit: number } {
    const choices = toChoices(this.areas, role.grants);
    return {
      view: countAtLeast(this.areas, choices, 'Read'),
      edit: countAtLeast(this.areas, choices, 'ReadWrite')
    };
  }

  select(role: IAccessRole): void {
    if (this.selected === role && !this.isNew) {
      this.close();
      return;
    }
    this.isNew = false;
    this.open(role, role.grants);
    if (role.id) this.location.go('roles/' + role.id);
  }

  addRole(copyFrom?: IAccessRole): void {
    this.isNew = true;
    const draft: IAccessRole = { name: '', builtIn: false, grants: {}, assignedStaffCount: 0, icon: ROLE_ICONS[0] };
    this.open(draft, copyFrom ? this.withinOwnAccess(copyFrom.grants) : {});
    if (copyFrom) {
      this.form.patchValue({ name: this.translate.instant('ROLES_COPY_OF', { name: this.roleName(copyFrom) }) });
      this.choicesDirty = true;
    }
    this.location.go('roles');
  }

  close(): void {
    this.selected = null;
    this.isNew = false;
    this.location.go('roles');
  }

  /** Start the editor's permissions from another role */
  startFrom(role: IAccessRole): void {
    if (!role) return;
    this.choices = toChoices(this.areas, this.withinOwnAccess(role.grants));
    this.choicesDirty = true;
  }

  setChoice(key: string, choice: AreaChoice): void {
    this.choices = { ...this.choices, [key]: choice };
    this.choicesDirty = true;
  }

  /** Sub-areas of a main area */
  childrenOf(key: string): AreaRow[] {
    return this.rows.filter(r => r.area.key.startsWith(key + '.'));
  }

  /**
   * Rows shown in the editor. Areas the user can't grant at all are left out unless the role already
   * has access there, and sub-areas show when their main area is opened or one of them is set on its own.
   */
  get visibleRows(): AreaRow[] {
    return this.rows.filter(row => {
      const relevant = this.permissions.has(row.area.key, 'Read') || effectiveChoice(row.area.key, this.choices) !== 'None';
      if (!relevant) return false;
      if (!row.hasParent) return true;
      const top = row.area.key.split('.')[0];
      return this.expanded.has(top) || this.childrenOf(top).some(c => this.choices[c.area.key] !== 'Inherit');
    });
  }

  /** Main area with sub-areas that can be opened or closed */
  isExpandable(row: AreaRow): boolean {
    return !row.hasParent && this.childrenOf(row.area.key).length > 0;
  }

  /** Sub-areas can't be closed while one of them is set on its own */
  isExpanded(row: AreaRow): boolean {
    return this.expanded.has(row.area.key) || this.childrenOf(row.area.key).some(c => this.choices[c.area.key] !== 'Inherit');
  }

  toggleExpanded(row: AreaRow): void {
    if (this.isExpanded(row) && !this.expanded.has(row.area.key)) return;
    if (this.expanded.has(row.area.key)) this.expanded.delete(row.area.key);
    else this.expanded.add(row.area.key);
  }

  /** Level the area ends up with, for the 'Same as above' hint */
  effective(key: string): AccessLevel {
    return effectiveChoice(key, this.choices);
  }

  /**
   * Translated name of the area whose page the sub-area needs, when the role gives the sub-area access
   * but not that page (e.g. Base stations without Devices); null when the sub-area takes effect
   */
  needsPageAccess(row: AreaRow): string | null {
    const page = row.hasParent ? unreachableFrom(row.area.key, this.choices, OWN_PAGE_AREAS) : null;
    if (!page) return null;
    const area = this.areas.find(a => a.key === page);
    return area ? this.translate.instant(area.displayNameKey) : page;
  }

  /** Levels above the user's own access on the area can't be granted */
  aboveOwnAccess(key: string, level: AccessLevel): boolean {
    return ACCESS_LEVEL_RANK[level] > ACCESS_LEVEL_RANK[this.permissions.resolve(key)];
  }

  /** 'Same as above' is blocked when the parent's level is above the user's own access on this area */
  inheritAboveOwnAccess(row: AreaRow): boolean {
    return row.hasParent && this.aboveOwnAccess(row.area.key, effectiveChoice(row.area.parentKey, this.choices));
  }

  levelLabel(level: AccessLevel): string {
    return this.translate.instant({ None: 'ACCESS_NONE', Read: 'ACCESS_READ', ReadWrite: 'ACCESS_READWRITE' }[level]);
  }

  get otherRoles(): IAccessRole[] {
    return this.roles.filter(r => r !== this.selected);
  }

  get canSave(): boolean {
    return !this.readOnly && this.form?.valid && (this.form.dirty || this.choicesDirty || this.isNew) &&
      !this.exceedsOwnAccess;
  }

  /**
   * The grants as saved, sub-areas resolved through their parents, give more than the user has: for
   * example Devices View & edit with Advanced settings on "Same as above" while the user only views
   * advanced settings. The API refuses such a role, so it can't be saved.
   */
  get exceedsOwnAccess(): boolean {
    return !!this.areas?.length && !!this.choices && !this.permissions.covers(toGrants(this.areas, this.choices));
  }

  save(): void {
    if (!this.canSave) return;
    this.saveSuccess = false;
    this.saveError = null;

    const request: ISaveAccessRole = {
      name: this.form.value.name.trim(),
      description: this.form.value.description || null,
      icon: this.form.value.icon || null,
      grants: toGrants(this.areas, this.choices)
    };

    const call = this.isNew
      ? this.api.addAccessRole(request)
      : this.api.updateAccessRole(this.selected.id, request);

    call.subscribe({
      next: saved => {
        this.saveSuccess = true;
        this.isNew = false;
        this.choicesDirty = false;
        this.form.markAsPristine();
        // A changed role may change the user's own permissions
        this.permissions.load(true).subscribe();
        this.load(saved.id, true);
        setTimeout(() => this.saveSuccess = false, 3000);
      },
      error: err => this.saveError = this.errorMessage(err)
    });
  }

  delete(): void {
    if (!this.selected?.id) return;
    const role = this.selected;
    this.dialog.open(DeleteRoleDialogComponent, {
      width: '50%',
      data: { name: role.name, assignedStaffCount: role.assignedStaffCount }
    }).afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.api.deleteAccessRole(role.id).subscribe({
        next: () => {
          this.close();
          this.permissions.load(true).subscribe();
          this.load();
        },
        error: err => this.saveError = this.errorMessage(err)
      });
    });
  }

  private open(role: IAccessRole, grants: Record<string, AccessLevel>): void {
    this.selected = role;
    this.saveSuccess = false;
    this.saveError = null;
    this.choices = toChoices(this.areas, grants);
    this.choicesDirty = false;
    this.expanded = new Set<string>();
    this.form = this.fb.group({
      name: [role.builtIn ? this.roleName(role) : role.name, [Validators.required, Validators.maxLength(100)]],
      description: [role.description ?? '', Validators.maxLength(255)],
      icon: [role.icon ?? ROLE_ICONS[0]]
    });
    if (this.readOnly) this.form.disable();
  }

  /**
   * Grants limited to what the user has, so a copied role can be saved. Compared per area after
   * resolving sub-areas through their parents: where a sub-area would inherit more than the user has
   * (the user's own sub-area is lower than its parent), it gets an explicit grant at the user's level.
   * The registry lists parents before their children.
   */
  private withinOwnAccess(grants: Record<string, AccessLevel>): Record<string, AccessLevel> {
    const limited: Record<string, AccessLevel> = {};
    for (const area of this.areas) {
      const copied = resolveGrant(grants, area.key);
      const own = this.permissions.resolve(area.key);
      const target = ACCESS_LEVEL_RANK[copied] > ACCESS_LEVEL_RANK[own] ? own : copied;
      if (resolveGrant(limited, area.key) !== target) limited[area.key] = target;
    }
    return limited;
  }

  private errorMessage(err: any): string {
    const detail = typeof err?.error === 'string' ? err.error : '';
    switch (err?.status) {
      case 403: return this.translate.instant('ROLES_ERROR_ABOVE_OWN_ACCESS') + (detail ? ' ' + detail : '');
      case 409: return this.translate.instant('ROLES_ERROR_CONFLICT') + (detail ? ' ' + detail : '');
      case 400: return this.translate.instant('ROLES_ERROR_INVALID') + (detail ? ' ' + detail : '');
      default: return this.translate.instant('ERROR');
    }
  }
}
